import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from 'react';
import {
  CalendarBlankIcon,
  CaretLeftIcon,
  CaretRightIcon,
  ClockIcon,
} from '@phosphor-icons/react';
import clsx from 'clsx';
import {
  formatCurrency,
  addDays,
  sameDay,
  dateKey,
  type Bill,
  type BillFrequency,
  type Occurrence,
  buildOccurrences,
} from '@budget/core';
import { IconButton } from '../IconButton';
import { ThirdPartyLogo } from '../ThirdPartyLogos';
import { useScope } from '../../hooks/useScope';

// Extracted from pages/BillsPage so the same calendar renders in two
// contexts: the Bills tab (default desktop sizing) and the kitchen kiosk
// (tall cells for portrait orientation). Behavior is unchanged from the
// original — only the cell height is parameterized.

export function fmt0(n: number): string {
  return formatCurrency(n, 'USD', 'en-US', 0);
}

export function fmtCents(n: number): string {
  return formatCurrency(n);
}

const compactNoDecimal = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 0,
});
function fmtDayTotal(n: number): string {
  return compactNoDecimal.format(n);
}

export function billCategoryLabel(bill: Bill): string {
  if (!bill.cashOutItem) return '';
  return bill.cashOutItem.replace(/\s*\([^)]*\)/g, '').trim();
}

const FREQUENCY_LABEL: Record<BillFrequency, string> = {
  monthly: 'Monthly',
  'semi-monthly': 'Semi-monthly',
  weekly: 'Weekly',
  biweekly: 'Bi-weekly',
  quarterly: 'Quarterly',
  'semi-annually': 'Semi-annually',
  yearly: 'Yearly',
  'one-time': 'One-time',
};

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// Auto-dismiss an open day popover after this long, so one left open on the
// kiosk doesn't linger indefinitely.
const AUTO_CLOSE_MS = 10_000;

export function incomeRingClass(bill: Bill): string {
  if (bill.type !== 'income') return '';
  if (bill.responsible === 'dakota') return 'ring-2 ring-dakota';
  if (bill.responsible === 'natalia') return 'ring-2 ring-natalia';
  return 'ring-2 ring-green';
}

export type MonthCalendarCellHeight = 'normal' | 'tall';

const CELL_HEIGHT_CLASS: Record<MonthCalendarCellHeight, string> = {
  // Original Bills-page sizing — desktop-tuned.
  normal: 'h-20',
  // Kitchen kiosk portrait — no height class at all. The cell is a grid
  // item in a `grid-rows-6` track (`minmax(0, 1fr)`) and stretches to fill
  // its track (default `align-self: stretch`), so cells grow and shrink
  // with the available vertical space. Deliberately NO `min-h` floor: an
  // 80px floor exceeded the per-row 1fr share when the slot was short, so
  // cells overran the 12px `gap-3` row gaps instead of shrinking.
  tall: '',
};

export interface CalendarEvent {
  id: string;
  title: string;
  /** ISO 8601 date-time for timed events, or YYYY-MM-DD for all-day. */
  start: string;
  end: string;
  allDay: boolean;
  /** Which Google Calendar the event came from; optional here because
   *  MonthCalendar only renders day-dots and doesn't differentiate by
   *  source. The data hook always sets this. */
  source?: 'family' | 'expirations' | 'holidays';
}

interface MonthCalendarProps {
  bills: Bill[];
  cursorMonth: Date;
  today: Date;
  onPrev: () => void;
  onNext: () => void;
  onToday: () => void;
  onSelectOccurrence: (occ: Occurrence) => void;
  cellHeight?: MonthCalendarCellHeight;
  /** Display the running monthly total under the month label. Defaults
   *  to true (the web Bills tab); the kiosk passes false to keep the
   *  header to just the month name. */
  showMonthlyTotal?: boolean;
  /** Google Calendar events from the shared family calendar. Each
   *  covered day gets a small white dot in the top-right corner, and
   *  event-only days become tappable to open the day popover. Passed by
   *  the kiosk's BillsWidget and the home screen's Calendar overlay; the
   *  Bills tab leaves this undefined so it stays bills-only. */
  events?: CalendarEvent[];
  /** Disable the hover tooltip on bill logos. The kiosk passes true
   *  because there's no hover on a touchscreen; the web Bills page
   *  leaves this false so the tooltip still works on a desktop. */
  disableLogoTooltips?: boolean;
}

// Parse a YYYY-MM-DD string as a local-time midnight Date (not UTC), so
// dateKey() matches the calendar grid's local-tz keys. Using `new Date(s)`
// on a bare date string parses as UTC and can shift one day east-of-GMT.
function parseLocalDate(s: string): Date {
  const [y, m, d] = s.slice(0, 10).split('-').map(Number);
  return new Date(y, (m ?? 1) - 1, d ?? 1);
}

export function MonthCalendar({
  bills,
  cursorMonth,
  today,
  onPrev,
  onNext,
  onToday,
  onSelectOccurrence,
  cellHeight = 'normal',
  showMonthlyTotal = true,
  events,
  disableLogoTooltips = false,
}: MonthCalendarProps) {
  // Calendar always renders 6 weeks (42 cells) so the grid height is stable
  // when stepping between months — avoids the page jumping.
  const monthStart = new Date(cursorMonth.getFullYear(), cursorMonth.getMonth(), 1);
  const gridStart = addDays(monthStart, -monthStart.getDay());
  const gridEnd = addDays(gridStart, 41);

  const occurrencesByDay = useMemo(() => {
    const map = new Map<string, Occurrence[]>();
    for (const occ of buildOccurrences(bills, gridStart, gridEnd)) {
      const key = dateKey(occ.date);
      const list = map.get(key) ?? [];
      list.push(occ);
      map.set(key, list);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bills, gridStart.getTime(), gridEnd.getTime()]);

  // Map of dateKey → events covering that day. Multi-day events show on
  // every day they span (with Google's exclusive-end convention for all-
  // day events handled below). Used both for the top-right dot indicator
  // (truthy = at least one event) AND as the source the DayPopover reads
  // when an event-only day is tapped.
  const eventsByDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    if (!events) return map;
    for (const event of events) {
      let cursor: Date;
      let lastDay: Date;
      if (event.allDay) {
        cursor = parseLocalDate(event.start);
        lastDay = addDays(parseLocalDate(event.end), -1); // end is exclusive
      } else {
        cursor = new Date(event.start);
        lastDay = new Date(event.end);
      }
      let safety = 400;
      while (cursor.getTime() <= lastDay.getTime() && safety-- > 0) {
        const key = dateKey(cursor);
        const list = map.get(key) ?? [];
        list.push(event);
        map.set(key, list);
        cursor = addDays(cursor, 1);
      }
    }
    return map;
  }, [events]);

  const monthLabel = cursorMonth.toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  });

  const { monthlyTotal, monthlyLeft } = useMemo(() => {
    let total = 0;
    let left = 0;
    for (const list of occurrencesByDay.values()) {
      for (const occ of list) {
        if (occ.date.getMonth() !== cursorMonth.getMonth()) continue;
        if (occ.bill.type === 'income') continue;
        total += occ.bill.amount;
        if (occ.date >= today) left += occ.bill.amount;
      }
    }
    return { monthlyTotal: total, monthlyLeft: left };
  }, [occurrencesByDay, cursorMonth, today]);

  const isCurrentMonth =
    today.getFullYear() === cursorMonth.getFullYear() &&
    today.getMonth() === cursorMonth.getMonth();

  const [openKey, setOpenKey] = useState<string | null>(null);
  const calendarRef = useRef<HTMLDivElement>(null);

  const { scope } = useScope();
  const todayPalette = (() => {
    if (scope === 'dakota') {
      return {
        bg: 'bg-dakota-soft',
        hover: 'hover:bg-dakota-glow',
        border: 'border-dakota',
        text: 'text-dakota',
        glow: 'glow-dakota',
      };
    }
    if (scope === 'natalia') {
      return {
        bg: 'bg-natalia-soft',
        hover: 'hover:bg-natalia-glow',
        border: 'border-natalia',
        text: 'text-natalia',
        glow: 'glow-natalia',
      };
    }
    return {
      bg: 'bg-green-soft',
      hover: 'hover:bg-green-glow',
      border: 'border-green',
      text: 'text-green',
      glow: 'glow-brand',
    };
  })();

  useEffect(() => {
    if (!openKey) return;
    function onPointerDown(e: PointerEvent) {
      if (!calendarRef.current?.contains(e.target as Node)) {
        setOpenKey(null);
      }
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpenKey(null);
    }
    // Auto-dismiss after 15s of inactivity so a popover left open on the
    // kiosk doesn't linger. Re-runs (and so restarts) whenever openKey changes.
    const autoClose = window.setTimeout(() => setOpenKey(null), AUTO_CLOSE_MS);
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.clearTimeout(autoClose);
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openKey]);

  useEffect(() => {
    setOpenKey(null);
  }, [cursorMonth]);

  const cellHeightClass = CELL_HEIGHT_CLASS[cellHeight];
  const fillHeight = cellHeight === 'tall';

  return (
    <div
      ref={calendarRef}
      className={clsx('flex flex-col gap-4', fillHeight && 'h-full')}
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <h2 className="text-2xl font-semibold">{monthLabel}</h2>
          {showMonthlyTotal && monthlyTotal > 0 && (
            <span className="tabular text-sm text-fg-tertiary">
              <span className="font-semibold">{fmt0(monthlyTotal)}</span>
              {isCurrentMonth && (
                <span className="text-xs"> ({fmt0(monthlyLeft)} left)</span>
              )}
            </span>
          )}
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={onToday}
            disabled={isCurrentMonth}
            aria-hidden={isCurrentMonth}
            tabIndex={isCurrentMonth ? -1 : 0}
            style={{ visibility: isCurrentMonth ? 'hidden' : 'visible' }}
            className="px-3 h-8 text-xs font-medium rounded-full border border-border-strong text-fg hover:bg-overlay transition-colors"
          >
            Reset
          </button>
          <div className="flex items-center gap-1.5">
            <IconButton
              onClick={onPrev}
              aria-label="Previous month"
              variant="outline"
              icon={<CaretLeftIcon size={14} weight="bold" />}
            />
            <IconButton
              onClick={onNext}
              aria-label="Next month"
              variant="outline"
              icon={<CaretRightIcon size={14} weight="bold" />}
            />
          </div>
        </div>
      </div>

      <div className="grid grid-cols-7 gap-3 px-1 max-md:gap-[3px] max-md:px-0">
        {WEEKDAY_LABELS.map((d) => (
          <div
            key={d}
            className="text-[0.6875rem] uppercase tracking-[0.08em] font-medium text-fg-tertiary text-center pb-1"
          >
            {d}
          </div>
        ))}
      </div>
      <div
        className={clsx(
          'grid grid-cols-7 gap-3 max-md:gap-[3px]',
          fillHeight && 'flex-1 min-h-0 grid-rows-6',
        )}
      >
        {Array.from({ length: 42 }, (_, i) => {
          const day = addDays(gridStart, i);
          const inMonth = day.getMonth() === cursorMonth.getMonth();
          const isToday = sameDay(day, today);
          const dKey = dateKey(day);
          const list = occurrencesByDay.get(dKey) ?? [];
          const billsOnly = list.filter((occ) => occ.bill.type !== 'income');
          const visible = billsOnly.slice(0, 3);
          const overflow = billsOnly.length - visible.length;
          const hasBills = list.length > 0;
          const billsTotal = billsOnly.reduce((sum, occ) => sum + occ.bill.amount, 0);
          const incomePersons: Array<'dakota' | 'natalia'> = [];
          {
            let hasD = false;
            let hasN = false;
            for (const occ of list) {
              if (occ.bill.type !== 'income') continue;
              if (
                occ.bill.responsible === 'dakota' ||
                occ.bill.responsible === 'shared'
              )
                hasD = true;
              if (
                occ.bill.responsible === 'natalia' ||
                occ.bill.responsible === 'shared'
              )
                hasN = true;
            }
            if (hasD) incomePersons.push('dakota');
            if (hasN) incomePersons.push('natalia');
          }
          const isOpen = openKey === dKey;
          const eventsForDay = eventsByDay.get(dKey) ?? [];
          const hasEvents = eventsForDay.length > 0;
          // Expiration-track events (warranties, registrations, etc.) get a
          // rose dot instead of the default neutral one so they read as a
          // heads-up. When a day mixes expirations with other events, both a
          // neutral and a rose dot render — the rose (expiration) dot always
          // sits on the right (see the dot row below).
          const hasExpiration = eventsForDay.some(
            (e) => e.source === 'expirations',
          );
          const hasOtherEvent = eventsForDay.some(
            (e) => e.source !== 'expirations',
          );
          const eventDotCount = (hasOtherEvent ? 1 : 0) + (hasExpiration ? 1 : 0);
          // Day is tappable when there's anything to show in the popover:
          // bills, income, or calendar events.
          const isClickable = hasBills || hasEvents;
          const col = i % 7;
          const row = Math.floor(i / 7);
          const flipRight = col >= 5;
          const flipUp = row >= 4;
          const paydayShared = incomePersons.length === 2;
          const paydayGradient = (() => {
            if (incomePersons.length === 0) return undefined;
            if (paydayShared)
              return `radial-gradient(circle at bottom left, var(--berg-green-soft), transparent 65%)`;
            const p = incomePersons[0];
            return `radial-gradient(circle at bottom left, var(--berg-${p}-soft), transparent 65%)`;
          })();
          // Expiration days get a rose wash from the top-right corner —
          // mirrors the payday radial treatment but anchored to the opposite
          // corner so the two can coexist on a day that's both a payday and
          // an expiration.
          const expirationGradient = hasExpiration
            ? `radial-gradient(circle at top right, var(--berg-rose-soft), transparent 65%)`
            : undefined;
          const cellGradient =
            [paydayGradient, expirationGradient].filter(Boolean).join(', ') ||
            undefined;
          return (
            <div
              key={i}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onClick={
                isClickable
                  ? () => setOpenKey((cur) => (cur === dKey ? null : dKey))
                  : undefined
              }
              onKeyDown={
                isClickable
                  ? (e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpenKey((cur) => (cur === dKey ? null : dKey));
                      }
                    }
                  : undefined
              }
              style={cellGradient ? { backgroundImage: cellGradient } : undefined}
              className={clsx(
                'relative rounded-xl border transition-colors max-md:rounded-none',
                cellHeightClass,
                !isToday && 'max-md:border-0',
                inMonth
                  ? isToday
                    ? todayPalette.bg
                    : 'bg-elevated'
                  : 'bg-overlay',
                isOpen
                  ? 'border-fg'
                  : isToday
                  ? clsx(todayPalette.border, todayPalette.glow)
                  : 'border-transparent',
                isClickable &&
                  (isToday
                    ? clsx(
                        'cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-secondary',
                        todayPalette.hover,
                      )
                    : 'cursor-pointer hover:bg-overlay md:hover:bg-subtle focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-secondary')
              )}
            >
              <div
                className={clsx(
                  'relative h-full w-full p-2 flex items-center justify-center',
                  !inMonth && 'opacity-40'
                )}
              >
                {visible.length > 0 && (
                  <div className="flex items-center justify-center gap-1 max-md:hidden">
                    {visible.map((occ, idx) => (
                      <span
                        key={`${occ.bill.name}-${idx}`}
                        className="w-7 h-7 rounded-full flex items-center justify-center"
                      >
                        <ThirdPartyLogo
                          location={occ.bill.name}
                          size={22}
                          noTooltip={disableLogoTooltips}
                        />
                      </span>
                    ))}
                  </div>
                )}
                {billsOnly.length === 1 && (
                  <span className="hidden max-md:inline-flex w-6 h-6 rounded-full items-center justify-center">
                    <ThirdPartyLogo
                      location={billsOnly[0].bill.name}
                      size={18}
                      noTooltip={disableLogoTooltips}
                    />
                  </span>
                )}
                {billsOnly.length > 1 && (
                  <span className="hidden max-md:inline-flex items-center justify-center min-w-5 h-5 px-1.5 rounded-full bg-overlay border border-border text-fg text-[0.6875rem] font-semibold tabular">
                    {billsOnly.length}
                  </span>
                )}
                {billsTotal > 0 && (
                  <span className="absolute top-1.5 left-2 max-md:left-1/2 max-md:-translate-x-1/2 text-[0.625rem] tabular text-fg-tertiary font-medium">
                    {fmtDayTotal(billsTotal)}
                  </span>
                )}
                {incomePersons.length > 0 && (
                  <div className="hidden md:flex absolute bottom-1.5 left-2 items-center gap-1">
                    <span
                      className={clsx(
                        'person-dot',
                        paydayShared
                          ? 'dot-shared'
                          : incomePersons[0] === 'dakota'
                          ? 'dot-dakota'
                          : 'dot-natalia',
                      )}
                    />
                  </div>
                )}
                {overflow > 0 && (
                  <span
                    className={clsx(
                      'absolute top-1.5 text-[0.625rem] tabular text-fg-tertiary font-medium max-md:hidden',
                      // Shift left to clear the event dot(s) at the top-right
                      // so they sit side by side instead of stacking. Two dots
                      // (mixed expiration + other event) need extra room.
                      eventDotCount >= 2
                        ? 'right-[1.875rem]'
                        : eventDotCount === 1
                        ? 'right-[1.125rem]'
                        : 'right-2',
                    )}
                  >
                    +{overflow}
                  </span>
                )}
                {hasEvents && (
                  <div className="absolute top-2 right-2 flex items-center gap-1">
                    {hasOtherEvent && (
                      <span className="person-dot bg-fg" aria-hidden="true" />
                    )}
                    {hasExpiration && (
                      <span
                        className="person-dot bg-rose-light dot-glow"
                        style={
                          {
                            '--dot-color': 'var(--color-rose-light)',
                          } as CSSProperties
                        }
                        aria-hidden="true"
                      />
                    )}
                  </div>
                )}
                <span
                  className={clsx(
                    'hidden md:inline absolute bottom-1.5 right-2 text-xs tabular',
                    isToday
                      ? clsx(todayPalette.text, 'font-semibold')
                      : 'text-fg-tertiary',
                  )}
                >
                  {day.getDate()}
                </span>
                <span
                  className={clsx(
                    'md:hidden absolute bottom-1 left-1/2 -translate-x-1/2 w-5 text-center text-xs tabular',
                    isToday
                      ? clsx(todayPalette.text, 'font-semibold')
                      : 'text-fg-tertiary',
                  )}
                >
                  {incomePersons.length >= 1 && (
                    <span
                      className={clsx(
                        'person-dot absolute right-full mr-0.5 top-1/2 -translate-y-1/2',
                        paydayShared
                          ? 'dot-shared'
                          : incomePersons[0] === 'dakota'
                          ? 'dot-dakota'
                          : 'dot-natalia',
                      )}
                    />
                  )}
                  {day.getDate()}
                </span>
              </div>
              {isOpen && (
                <DayPopover
                  date={day}
                  occurrences={list}
                  events={eventsForDay}
                  flipRight={flipRight}
                  flipUp={flipUp}
                  disableLogoTooltips={disableLogoTooltips}
                  onSelect={(occ) => {
                    onSelectOccurrence(occ);
                    setOpenKey(null);
                  }}
                  onClose={() => setOpenKey(null)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function eventTimeLabel(event: CalendarEvent): string {
  if (event.allDay) return 'All day';
  const d = new Date(event.start);
  const h12 = ((d.getHours() + 11) % 12) + 1;
  const m = d.getMinutes();
  const ampm = d.getHours() < 12 ? 'AM' : 'PM';
  return `${h12}:${m.toString().padStart(2, '0')}${ampm}`;
}

function eventSourceLabel(event: CalendarEvent): string | null {
  if (event.source === 'holidays') return 'US holiday';
  if (event.source === 'expirations') return 'Reminder';
  return null;
}

function DayPopover({
  date,
  occurrences,
  events,
  flipRight,
  flipUp,
  disableLogoTooltips,
  onSelect,
  onClose,
}: {
  date: Date;
  occurrences: Occurrence[];
  events: CalendarEvent[];
  flipRight: boolean;
  flipUp: boolean;
  disableLogoTooltips: boolean;
  onSelect: (occ: Occurrence) => void;
  onClose: () => void;
}) {
  const heading = date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
  const sortedOccurrences = useMemo(() => {
    return [...occurrences].sort((a, b) => {
      const aIncome = a.bill.type === 'income' ? 1 : 0;
      const bIncome = b.bill.type === 'income' ? 1 : 0;
      return aIncome - bIncome;
    });
  }, [occurrences]);
  // All-day events first, then timed by start. Events without a
  // dateTime sort to the top of the list naturally with localeCompare on
  // the date-only string, but we make it explicit so timed-event mixing
  // doesn't depend on the format details.
  const sortedEvents = useMemo(() => {
    return [...events].sort((a, b) => {
      if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
      return a.start.localeCompare(b.start);
    });
  }, [events]);
  // The flipRight column heuristic picks a starting anchor, but on narrow
  // calendars (the kiosk grid) a w-64 popover can still overrun the screen.
  // Measure after mount and shift back inside the viewport.
  const popRef = useRef<HTMLDivElement>(null);
  const [shiftX, setShiftX] = useState(0);
  useLayoutEffect(() => {
    const rect = popRef.current?.getBoundingClientRect();
    if (!rect) return;
    const margin = 12;
    if (rect.right > window.innerWidth - margin) {
      setShiftX(window.innerWidth - margin - rect.right);
    } else if (rect.left < margin) {
      setShiftX(margin - rect.left);
    }
  }, []);
  return (
    <div
      ref={popRef}
      role="dialog"
      onClick={(e) => e.stopPropagation()}
      style={shiftX ? { transform: `translateX(${shiftX}px)` } : undefined}
      className={clsx(
        'absolute z-20 w-64 rounded-xl border border-border bg-elevated shadow-lg p-3 flex flex-col gap-2 text-left cursor-default',
        flipUp ? 'bottom-full mb-2' : 'top-full mt-2',
        flipRight ? 'right-0' : 'left-0'
      )}
    >
      <div className="text-xs uppercase tracking-[0.08em] font-medium text-fg-tertiary">
        {heading}
      </div>
      <div className="flex flex-col gap-1">
        {sortedOccurrences.map((occ, i) => (
          <button
            key={`${occ.bill.name}-${i}`}
            type="button"
            onClick={() => onSelect(occ)}
            className="flex items-center gap-2.5 py-1.5 px-2 -mx-2 rounded-lg text-left hover:bg-overlay focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-secondary transition-colors cursor-pointer"
          >
            <span
              className={clsx(
                'w-7 h-7 flex-none flex items-center justify-center rounded-full',
                incomeRingClass(occ.bill),
              )}
            >
              <ThirdPartyLogo
                location={occ.bill.name}
                size={24}
                noTooltip={disableLogoTooltips}
              />
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm truncate">{occ.bill.name}</div>
              <div className="text-[0.6875rem] text-fg-tertiary truncate">
                {FREQUENCY_LABEL[occ.bill.frequency]}
                {billCategoryLabel(occ.bill) && <> · {billCategoryLabel(occ.bill)}</>}
                {occ.bill.account && <> · {occ.bill.account}</>}
              </div>
            </div>
            {occ.bill.amount > 0 && (
              <div
                className={clsx(
                  'tabular text-sm font-semibold flex-none',
                  occ.bill.type === 'income' && 'delta-pos',
                )}
              >
                {occ.bill.type === 'income' ? '+' : ''}
                {fmtCents(occ.bill.amount)}
              </div>
            )}
          </button>
        ))}
        {sortedEvents.map((event) => {
          const isExpiration = event.source === 'expirations';
          const sourceLabel = eventSourceLabel(event);
          const timeLabel = eventTimeLabel(event);
          return (
            <button
              key={event.id}
              type="button"
              onClick={onClose}
              className="flex items-center gap-2.5 py-1.5 px-2 -mx-2 rounded-lg text-left hover:bg-overlay focus:outline-none focus-visible:ring-2 focus-visible:ring-fg-secondary transition-colors cursor-pointer"
            >
              <span className="w-7 h-7 flex-none flex items-center justify-center rounded-full">
                {isExpiration ? (
                  <ClockIcon
                    size={20}
                    weight="duotone"
                    className="text-rose-light"
                  />
                ) : (
                  <CalendarBlankIcon
                    size={20}
                    weight="duotone"
                    className="text-fg-secondary"
                  />
                )}
              </span>
              <div className="flex-1 min-w-0">
                <div
                  className={clsx(
                    'font-medium text-sm truncate',
                    isExpiration && 'text-rose-light',
                  )}
                >
                  {event.title}
                </div>
                <div className="text-[0.6875rem] text-fg-tertiary truncate">
                  {timeLabel}
                  {sourceLabel && <> · {sourceLabel}</>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
